import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Empty, Modal, Skeleton } from 'antd'
import { useReactToPrint } from 'react-to-print'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { GroupCollectionSettings } from './group-collection-settings.tsx'
import { ReceiptDocument } from './receipt-document.tsx'
import { snapshotObject } from './utils/finance-snapshot.ts'
import { receiptPrice } from './utils/receipt-data.ts'
import { copyReceiptImage } from './utils/receipt-image.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

const loadPdfExporter = () => import('./utils/receipt-pdf.tsx')

export function ReceiptWorkspace({
  ids,
  revisionNumber,
  canManage
}: {
  ids: number[]
  revisionNumber?: number
  canManage: boolean
}) {
  const trpc = useTRPC(),
    { message } = App.useApp()
  const cache = useQueryClient()
  const [settingsGroupId, setSettingsGroupId] = useState<number>()
  const query = useQuery({
    ...trpc.bill.groupBatchTargets.queryOptions({ mode: 'ids', ids }),
    enabled: !revisionNumber
  })
  const historical = useQuery({
    ...trpc.bill.groupDetail.queryOptions({ groupId: ids[0], revisionNumber }),
    enabled: !!revisionNumber
  })
  const source = revisionNumber ? historical : query
  const [activeId, setActiveId] = useState<number>()
  const [imageExporting, setImageExporting] = useState(false)
  const [pdfExporting, setPdfExporting] = useState<'current' | 'all'>()
  const [pdfFile, setPdfFile] = useState<{ url: string; name: string }>()
  const exporting = imageExporting || !!pdfExporting
  useEffect(() => {
    if (!pdfFile) {
      return
    }
    return () => URL.revokeObjectURL(pdfFile.url)
  }, [pdfFile])
  const groupSource = (() => {
    if (revisionNumber) {
      if (historical.data) {
        return [historical.data]
      }
      return []
    }
    return query.data ?? []
  })()
  const groups = groupSource.filter((group) => group.status !== 'draft')
  const current = groups.find((group) => group.id === activeId) ?? groups.at(0)
  const settingsGroup = groups.find((group) => group.id === settingsGroupId)
  const qrIds = [
    ...new Set(
      groups.flatMap((g) => {
        const id = snapshotObject(g.accountSnapshot).qrFileId
        return typeof id === 'number' ? [id] : []
      })
    )
  ]
  const urls = useQuery({
    ...trpc.bill.groupReceiptImages.queryOptions({ groupIds: ids, revisionNumber }),
    enabled: qrIds.length > 0
  })
  const qrUrl = (group: typeof current) =>
    urls.data?.find((file) => file.id === snapshotObject(group?.accountSnapshot).qrFileId)?.url
  const currentRef = useRef<HTMLElement>(null),
    allRef = useRef<HTMLDivElement>(null)
  const printCurrent = useReactToPrint({
    contentRef: currentRef,
    documentTitle: current?.groupNo ?? '收款通知单',
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }',
    onPrintError: () => {
      void message.error('打印失败，请重试')
    }
  })
  const printAll = useReactToPrint({
    contentRef: allRef,
    documentTitle: '收据批量打印',
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }',
    onPrintError: () => {
      void message.error('打印失败，请重试')
    }
  })
  const ready = !!current && !source.isFetching && (!qrIds.length || urls.isSuccess)
  const exportImage = async () => {
    if (!currentRef.current || !current || !ready || exporting) {
      return
    }
    setImageExporting(true)
    try {
      await copyReceiptImage(currentRef.current)
      void message.success('收款单图片已复制到剪贴板，可直接粘贴')
    } catch (error) {
      void message.error(error instanceof Error ? `图片复制失败：${error.message}` : '图片复制失败，请重试')
    }
    setImageExporting(false)
  }
  const exportPdf = async (scope: 'current' | 'all') => {
    if (!current || !ready || exporting) {
      return
    }
    setPdfExporting(scope)
    try {
      const { createReceiptPdf } = await loadPdfExporter()
      const selected = scope === 'current' ? [current] : groups
      const blob = await createReceiptPdf(selected.map((group) => ({ group, qrUrl: qrUrl(group) })))
      setPdfFile({
        url: URL.createObjectURL(blob),
        name: scope === 'current' ? `${current.groupNo}-收款单.pdf` : `收据汇总-${groups.length}份.pdf`
      })
    } catch (error) {
      void message.error(error instanceof Error ? error.message : 'PDF 生成失败，请重试')
    }
    setPdfExporting(undefined)
  }
  return (
    <main className="bg-surface-muted fixed inset-0 z-50 flex flex-col">
      <WorkspaceHeader>
        <div>
          <h1 className="m-0 text-base font-semibold">收款通知单工作台</h1>
          <p className="text-content-muted m-0 text-xs">
            {groups.length} 个账单组 · {groups.length} 份收款通知单
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {canManage && !revisionNumber && current && current.status !== 'voided' && (
            <Button disabled={source.isFetching || exporting} onClick={() => setSettingsGroupId(current.id)}>
              归属月份 / 催收设置
            </Button>
          )}
          <Button onClick={() => void source.refetch()} disabled={exporting}>
            刷新
          </Button>
          <Button disabled={!ready || exporting} loading={imageExporting} onClick={() => void exportImage()}>
            复制当前图片
          </Button>
          <Button disabled={!ready || exporting} onClick={() => printCurrent()}>
            打印当前
          </Button>
          <Button disabled={!ready || exporting} onClick={() => printAll()}>
            打印全部
          </Button>
          <Button
            disabled={!ready || exporting}
            loading={pdfExporting === 'current'}
            onClick={() => void exportPdf('current')}
          >
            导出当前 PDF
          </Button>
          <Button
            type="primary"
            disabled={!ready || exporting}
            loading={pdfExporting === 'all'}
            onClick={() => void exportPdf('all')}
          >
            导出全部 PDF
          </Button>
        </div>
      </WorkspaceHeader>
      {source.isError ? (
        <Alert
          className="m-4"
          type="error"
          title={source.error.message}
          action={<Button onClick={() => void source.refetch()}>重试</Button>}
        />
      ) : source.isPending ? (
        <Skeleton active className="p-6" />
      ) : !current ? (
        <Empty className="m-12" description="暂无已出单收据，草稿需先确认出单" />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3 md:flex-row">
          <nav
            aria-label="收据账单组"
            className="bg-surface flex max-h-48 shrink-0 gap-2 overflow-auto rounded-lg p-2 md:max-h-none md:w-64 md:flex-col"
          >
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-current={g.id === current.id ? 'true' : undefined}
                disabled={exporting}
                onClick={() => setActiveId(g.id)}
                className={cn(
                  'min-w-52 rounded-md border border-transparent p-3 text-left transition-colors focus-visible:outline-2',
                  g.id === current.id ? 'bg-accent/10 text-accent border-accent/20' : 'hover:bg-surface-muted'
                )}
              >
                <span className="block text-sm font-medium">{g.customerName}</span>
                <span className="mt-1 block text-xs">{g.groupNo}</span>
                <span className="text-content-muted mt-2 block text-xs">
                  ¥{receiptPrice(g.totalAmountSnapshot)} · {g.status === 'voided' ? '已作废' : '已出单'}
                </span>
              </button>
            ))}
          </nav>
          <section aria-label="收据预览" className="min-w-0 flex-1 overflow-auto rounded-lg">
            {urls.isError && (
              <Alert
                className="mb-3"
                type="error"
                title="收款二维码加载失败，请重试后导出"
                action={<Button onClick={() => void urls.refetch()}>重试</Button>}
              />
            )}
            <ReceiptDocument ref={currentRef} group={current} qrUrl={qrUrl(current)} />
          </section>
        </div>
      )}
      {settingsGroup && (
        <GroupCollectionSettings
          key={settingsGroup.id}
          group={settingsGroup}
          onClose={() => setSettingsGroupId(undefined)}
          onSaved={async () => {
            await refreshMoneyQueries(cache, trpc)
          }}
        />
      )}
      <Modal
        open={!!pdfFile}
        title="收款单 PDF 已生成"
        onCancel={() => setPdfFile(undefined)}
        footer={
          pdfFile && (
            <Button type="primary" href={pdfFile.url} download={pdfFile.name}>
              下载 PDF
            </Button>
          )
        }
      >
        <p>{pdfFile?.name}</p>
        {pdfFile && (
          <a href={pdfFile.url} target="_blank" rel="noopener noreferrer">
            在新标签页预览 PDF
          </a>
        )}
      </Modal>
      <div className="hidden">
        <div ref={allRef}>
          {groups.map((g) => (
            <ReceiptDocument key={g.id} group={g} qrUrl={qrUrl(g)} />
          ))}
        </div>
      </div>
    </main>
  )
}
